import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DisplayFieldConfiguration } from '@seahorse/domain';
import { ShInputComponent, ShNumberComponent } from '@seahorse/components';
import { ShCellDirective, ShTableComponent } from '@seahorse/shell';
import type { ShColumn } from '@seahorse/shell';
import type { ActionMenuItem } from '@seahorse/ui';

/**
 * The per-field display configuration of a list of displayed fields (sub
 * display fields, sort order, column name), one row per configured field.
 * Edits the rows in place; removing a row emits the new list
 * (`configurationChange`) and the removed field (`fieldRemove`).
 */
@Component({
  selector: 'su-field-configuration-template',
  templateUrl: './field-configuration.template.html',
  imports: [
    CommonModule,
    TranslateModule,
    ShTableComponent,
    ShCellDirective,
    ShInputComponent,
    ShNumberComponent,
  ],
})
export class FieldConfigurationTemplate {
  @Input() configuration: DisplayFieldConfiguration[] | null = [];
  @Output() configurationChange = new EventEmitter<
    DisplayFieldConfiguration[]
  >();

  @Output() fieldRemove = new EventEmitter<string>();

  // Stable fields: they feed sh-table.
  readonly columns: ShColumn<DisplayFieldConfiguration>[] = [
    {
      key: 'field',
      header: 'shared.terms.field',
      value: (row) => row.name ?? row.field,
    },
    {
      key: 'subDisplayFields',
      header: 'uiModules.displayedFields',
      type: 'custom',
    },
    { key: 'sortField', header: 'uiModules.sorting', type: 'custom' },
    { key: 'columnName', header: 'uiModules.columnName', type: 'custom' },
  ];

  readonly rowActions: ActionMenuItem<DisplayFieldConfiguration>[] = [
    {
      label: 'shared.terms.remove',
      icon: 'fa-regular fa-trash',
      danger: true,
      command: (row) => this.removeField(row.field),
    },
  ];

  removeField(systemCode: string) {
    const fieldConfigs = this.configuration;

    if (!fieldConfigs) {
      return;
    }

    if (!fieldConfigs.some((x) => x.field === systemCode)) {
      return;
    }

    this.configuration = fieldConfigs.filter((x) => x.field !== systemCode);
    this.configurationChange.emit(this.configuration);
    this.fieldRemove.emit(systemCode);
  }
}
